import { useLayoutEffect, useRef, useState } from 'react';
import { PanelId, usePanels } from './use-panels';

const FLYOUT_GAP = 8;

export type FlyoutPosition = { top: number; right: number };

type UseFlyoutPositionResult = {
  /** True si el panel está abierto (vía usePanels). */
  open: boolean;
  /** Ref para colgar en el elemento trigger (TabBtn wrapper). */
  triggerRef: React.RefObject<HTMLDivElement | null>;
  /** Posición calculada del popover (null hasta que se mida el trigger). */
  pos: FlyoutPosition | null;
  /** Toggle open/close del panel. */
  toggle: () => void;
  /** Cierra el panel explícitamente (útil cuando un item del popover termina su acción). */
  close: () => void;
};

/**
 * Lógica compartida para popovers que se renderean a la izquierda de un trigger
 * en el sidebar derecho del editor: lee el rect del trigger y devuelve
 * posición absoluta (top + right) para usar con `position: fixed` en un portal.
 *
 * Usado por FlyoutTab (popovers de íconos: Subir/Texto/Elementos) y por
 * componentes con popovers de contenido custom (SoundPanel).
 */
export const useFlyoutPosition = (panelId: PanelId): UseFlyoutPositionResult => {
  const { isOpen, toggle, close } = usePanels();
  const open = isOpen(panelId);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<FlyoutPosition | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.top, right: window.innerWidth - rect.left + FLYOUT_GAP });
  }, [open]);

  return {
    open,
    triggerRef,
    pos,
    toggle: () => toggle(panelId),
    close
  };
};
